/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * FuzzyVRMImageDialog.js
 * @description Modal dialog component for viewing and navigating through VRM images.
 * Displays entry and exit images captured from the VRM system with image navigation controls.
 * This is a presentational component that uses the ImageViewer sub-component for image display.
 *
 * @component
 * @example
 * return (
 *   <FuzzyVRMImageDialog
 *     open={isOpen}
 *     onClose={handleClose}
 *     images={allImages}
 *     currentIndex={activeImageIndex}
 *     onChangeIndex={setActiveImageIndex}
 *   />
 * )
 */
import React from 'react'
import { Dialog, DialogTitle, DialogContent } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ImageViewer from '../../../components/ImageViewer/ImageViewer'

/**
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Array} props.images - Array of image objects to display
 * @param {string} props.rowVrmNo - Selected row VRM number for dialog title
 * @param {number} props.currentIndex - Current index of the displayed image
 * @param {Function} props.onChangeIndex - Callback when image index changes
 * @returns {JSX.Element} The rendered image dialog component
 */
const FuzzyVRMImageDialog = ({
  open,
  onClose,
  images,
  rowVrmNo,
  currentIndex,
  onChangeIndex,
}) => {
  const dialogTitle = rowVrmNo ? `${rowVrmNo} - Images` : 'Images'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fuzzyVRM-viewImages-dialog"
      aria-labelledby="fuzzyVRM-viewImages-dialog-title"
    >
      <div className="fuzzyVRM-viewImages-dialog__title">
        <DialogTitle id="fuzzyVRM-viewImages-dialog-title">
          {dialogTitle}
        </DialogTitle>
        <CloseIcon
          className="fuzzyVRM-viewImages-dialog__close-icon"
          onClick={onClose}
        />
      </div>
      <DialogContent>
        <ImageViewer
          images={images}
          currentIndex={currentIndex}
          onChangeIndex={onChangeIndex}
          showImageName
        />
      </DialogContent>
    </Dialog>
  )
}

export default FuzzyVRMImageDialog
